"use client";

import { useEffect, useState } from "react";

export type TocHeading = {
    /** id attribute value on the heading in the article body */
    slug: string;
    /** Plain-text heading content */
    text: string;
    /** 2 for h2, 3 for h3, etc. — drives left padding for nesting */
    level: number;
};

type Props = {
    headings: TocHeading[];
};

/**
 * Sticky table-of-contents sidebar. Pinned to the viewport while
 * scrolling through the article body. Highlights the heading
 * currently in view via IntersectionObserver — same UX Outrank's
 * blog uses, and it scales to articles with 30+ headings without
 * the user having to manually find their place.
 *
 * Why client-side IntersectionObserver instead of CSS scroll-driven?
 * scroll-driven animations are still partial in Safari and the API
 * does not give us "which heading is currently active" — only whether
 * a single element is in view. IntersectionObserver lets us track
 * every heading at once and pick the one closest to the top.
 *
 * Renders nothing when there are < 2 headings (a single-section
 * article doesn't benefit from a TOC).
 */
export default function BlogStickyToc({ headings }: Props) {
    const [activeSlug, setActiveSlug] = useState<string | null>(headings[0]?.slug ?? null);

    useEffect(() => {
        if (headings.length === 0) return;

        // rootMargin shifts the "viewport" used for intersection
        // detection: top 100px hidden so a heading already past the
        // sticky article header still counts as "visible"; bottom
        // 70% hidden so we always pick the heading nearest the top
        // of the visible window, never the bottom one.
        const observer = new IntersectionObserver(
            (entries) => {
                // Find the entry that's intersecting AND closest to
                // the top of the page — that's the "current" heading.
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveSlug(visible[0].target.id);
                }
            },
            { rootMargin: "-100px 0px -70% 0px", threshold: 0 },
        );

        const elements: HTMLElement[] = [];
        for (const h of headings) {
            const el = document.getElementById(h.slug);
            if (el) {
                observer.observe(el);
                elements.push(el);
            }
        }

        return () => {
            for (const el of elements) observer.unobserve(el);
            observer.disconnect();
        };
    }, [headings]);

    if (headings.length < 2) return null;

    return (
        <nav className="blog-sticky-toc" aria-label="Table of contents">
            <div className="blog-sticky-toc-title">Table of contents</div>
            <ol className="blog-sticky-toc-list">
                {headings.map((h) => {
                    const isActive = h.slug === activeSlug;
                    return (
                        <li
                            key={h.slug}
                            data-level={h.level}
                            className={isActive ? "is-active" : undefined}
                        >
                            <a href={`#${h.slug}`}>{h.text}</a>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
